/**
 * Product Testing Media Proof Block Controller
 * Handles modal lightboxes, inline expandable media, and multi-instance audio/video coordination.
 */
(function () {
  'use strict';

  // Global state
  let modalContainer = null;
  let activeTrigger = null;
  let activeAudioElement = null;
  const directAudioInstances = new Set();

  /**
   * Stops all active media playing across the entire page.
   * Resets any active soundwave / playing indicators.
   */
  function stopAllMedia(exceptElement) {
    // Stop all DOM audio elements
    document.querySelectorAll('audio').forEach((audio) => {
      if (audio !== exceptElement && !audio.paused) {
        audio.pause();
      }
    });

    // Stop all direct Audio() objects
    directAudioInstances.forEach((audio) => {
      if (audio !== exceptElement && !audio.paused) {
        audio.pause();
      }
    });

    // Stop all video elements
    document.querySelectorAll('video').forEach((video) => {
      if (video !== exceptElement && !video.paused) {
        video.pause();
      }
    });

    // Reset all button playing states
    document.querySelectorAll('[data-pt-media-proof]').forEach((btn) => {
      if (!exceptElement || !btn.contains(exceptElement)) {
        btn.classList.remove('is-playing');
      }
    });
  }

  /**
   * Lazily creates the singleton modal container if not already in DOM.
   */
  function getModal() {
    if (modalContainer && document.body.contains(modalContainer)) {
      return modalContainer;
    }

    let modal = document.querySelector('[data-pt-media-modal]');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'pt-media-modal';
      modal.setAttribute('data-pt-media-modal', '');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Media Proof Preview');

      modal.innerHTML = `
        <div class="pt-media-modal__backdrop" data-pt-modal-close></div>
        <div class="pt-media-modal__dialog" role="document">
          <button type="button" class="pt-media-modal__close" data-pt-modal-close aria-label="Close preview">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="pt-media-modal__header">
            <h4 class="pt-media-modal__title" data-pt-modal-title></h4>
            <p class="pt-media-modal__subtext" data-pt-modal-subtext></p>
          </div>
          <div class="pt-media-modal__body" data-pt-modal-body></div>
        </div>
      `;

      document.body.appendChild(modal);

      // Event listeners for close
      modal.querySelectorAll('[data-pt-modal-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
      });

      // Trap focus & ESC key inside modal
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeModal();
          return;
        }

        if (e.key === 'Tab') {
          const focusable = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), video, audio'
          );
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      });
    }

    modalContainer = modal;
    return modalContainer;
  }

  /**
   * Opens the media proof modal with configured content.
   */
  function openModal(btn) {
    const modal = getModal();
    const mediaType = btn.dataset.mediaType || 'video';
    const title = btn.dataset.mediaTitle || '';
    const subtext = btn.dataset.mediaSubtext || '';
    const videoSrc = btn.dataset.videoSrc;
    const audioSrc = btn.dataset.audioSrc;
    const imageSrc = btn.dataset.imageSrc;
    const posterSrc = btn.dataset.posterSrc;

    activeTrigger = btn;
    stopAllMedia();

    // Populate header
    const titleEl = modal.querySelector('[data-pt-modal-title]');
    const subtextEl = modal.querySelector('[data-pt-modal-subtext]');
    const bodyEl = modal.querySelector('[data-pt-modal-body]');

    if (titleEl) titleEl.textContent = title;
    if (subtextEl) {
      subtextEl.textContent = subtext;
      subtextEl.style.display = subtext ? 'block' : 'none';
    }

    bodyEl.innerHTML = '';

    if (mediaType === 'video') {
      if (videoSrc) {
        // Check if YouTube or Vimeo URL
        if (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be') || videoSrc.includes('vimeo.com')) {
          const iframe = document.createElement('iframe');
          iframe.className = 'pt-media-modal__video';
          iframe.allow = 'autoplay; encrypted-media; fullscreen';
          iframe.allowFullscreen = true;
          iframe.src = videoSrc.includes('watch?v=')
            ? videoSrc.replace('watch?v=', 'embed/')
            : videoSrc;
          bodyEl.appendChild(iframe);
        } else {
          const video = document.createElement('video');
          video.className = 'pt-media-modal__video';
          video.controls = true;
          video.playsInline = true;
          video.autoplay = true;
          video.preload = 'auto';
          if (posterSrc) video.poster = posterSrc;
          video.src = videoSrc;
          bodyEl.appendChild(video);

          video.play().catch(() => {});
        }
      } else {
        bodyEl.innerHTML = `
          <div class="pt-media-proof__empty-notice">
            <p><strong>Video not configured</strong></p>
            <p>Select or upload a video in the Shopify Theme Editor sidebar under this block's settings.</p>
          </div>
        `;
      }
    } else if (mediaType === 'audio') {
      if (audioSrc) {
        const audioWrapper = document.createElement('div');
        audioWrapper.className = 'pt-media-modal__audio-box';
        audioWrapper.innerHTML = `
          <div class="pt-media-modal__audio-visualizer" aria-hidden="true">
            <span></span><span></span><span></span><span></span><span></span><span></span><span></span><span></span>
          </div>
          <audio class="pt-media-modal__audio" controls autoplay src="${audioSrc}"></audio>
        `;
        bodyEl.appendChild(audioWrapper);

        const audio = audioWrapper.querySelector('audio');
        activeAudioElement = audio;

        audio.addEventListener('play', () => {
          audioWrapper.classList.add('is-playing');
          btn.classList.add('is-playing');
        });
        audio.addEventListener('pause', () => {
          audioWrapper.classList.remove('is-playing');
          btn.classList.remove('is-playing');
        });
        audio.addEventListener('ended', () => {
          audioWrapper.classList.remove('is-playing');
          btn.classList.remove('is-playing');
        });

        audio.play().catch(() => {});
      } else {
        bodyEl.innerHTML = `
          <div class="pt-media-proof__empty-notice">
            <p><strong>Audio file URL not configured</strong></p>
            <p>Upload an MP3 in Shopify Admin > Content > Files and paste the file URL under this block's "Audio Source" settings.</p>
          </div>
        `;
      }
    } else if (mediaType === 'image') {
      if (imageSrc || posterSrc) {
        const img = document.createElement('img');
        img.className = 'pt-media-modal__image';
        img.src = imageSrc || posterSrc;
        img.alt = title;
        img.loading = 'eager';
        bodyEl.appendChild(img);
      } else {
        bodyEl.innerHTML = `
          <div class="pt-media-proof__empty-notice">
            <p><strong>Image not selected</strong></p>
            <p>Select an image in the Shopify Theme Editor sidebar under this block's "Image Source" settings.</p>
          </div>
        `;
      }
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus close button
    const closeBtn = modal.querySelector('.pt-media-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  /**
   * Closes the media proof modal and safely halts any media playback.
   */
  function closeModal() {
    if (!modalContainer || !modalContainer.classList.contains('is-open')) return;

    // Pause and clean up any media inside modal
    const video = modalContainer.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    const audio = modalContainer.querySelector('audio');
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }

    const iframe = modalContainer.querySelector('iframe');
    if (iframe) {
      iframe.src = '';
    }

    modalContainer.classList.remove('is-open');
    modalContainer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    const bodyEl = modalContainer.querySelector('[data-pt-modal-body]');
    if (bodyEl) bodyEl.innerHTML = '';

    if (activeTrigger) {
      activeTrigger.classList.remove('is-playing');
      activeTrigger.focus();
      activeTrigger = null;
    }
  }

  /**
   * Handles inline expandable media triggers.
   */
  function toggleInline(btn) {
    const wrapper = btn.closest('.pt-media-proof-wrapper');
    if (!wrapper) return;

    const drawer = wrapper.querySelector('[data-pt-inline-drawer]');
    if (!drawer) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    const mediaType = btn.dataset.mediaType;

    if (isOpen) {
      // Close drawer
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.remove('is-expanded');
      drawer.hidden = true;

      // Stop any media playing inside this drawer
      const video = drawer.querySelector('video');
      if (video && !video.paused) video.pause();

      const audio = drawer.querySelector('audio');
      if (audio && !audio.paused) {
        audio.pause();
        btn.classList.remove('is-playing');
      }
    } else {
      // Open drawer
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-expanded');
      drawer.hidden = false;

      if (mediaType === 'video') {
        const video = drawer.querySelector('video');
        if (video) {
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
          }
          stopAllMedia(video);
          video.play().catch(() => {});
        }
      } else if (mediaType === 'audio') {
        const audio = drawer.querySelector('audio');
        if (audio) {
          if (audio.dataset.src && !audio.src) {
            audio.src = audio.dataset.src;
          }
          stopAllMedia(audio);

          audio.onplay = () => btn.classList.add('is-playing');
          audio.onpause = () => btn.classList.remove('is-playing');
          audio.onended = () => btn.classList.remove('is-playing');

          audio.play().catch(() => {});
        }
      }
    }
  }

  /**
   * Direct audio playback toggle (for inline pill without full drawer).
   */
  function toggleDirectAudio(btn) {
    const audioSrc = btn.dataset.audioSrc;
    if (!audioSrc) return;

    if (!btn._audioInstance) {
      btn._audioInstance = new Audio(audioSrc);
      btn._audioInstance.preload = 'none';
      directAudioInstances.add(btn._audioInstance);

      btn._audioInstance.addEventListener('play', () => {
        btn.classList.add('is-playing');
      });
      btn._audioInstance.addEventListener('pause', () => {
        btn.classList.remove('is-playing');
      });
      btn._audioInstance.addEventListener('ended', () => {
        btn.classList.remove('is-playing');
      });
    }

    const audio = btn._audioInstance;

    if (!audio.paused) {
      audio.pause();
    } else {
      stopAllMedia(audio);
      audio.play().catch(() => {});
    }
  }

  /**
   * Main Click Handler Delegate
   */
  function handleMediaProofClick(e) {
    const btn = e.target.closest('[data-pt-media-proof]');
    if (!btn) return;

    e.preventDefault();

    const openBehavior = btn.dataset.openBehavior || 'modal';
    const mediaType = btn.dataset.mediaType;

    if (openBehavior === 'modal') {
      openModal(btn);
    } else if (openBehavior === 'inline') {
      const wrapper = btn.closest('.pt-media-proof-wrapper');
      const drawer = wrapper ? wrapper.querySelector('[data-pt-inline-drawer]') : null;

      if (drawer) {
        toggleInline(btn);
      } else if (mediaType === 'audio') {
        toggleDirectAudio(btn);
      }
    }
  }

  /**
   * Initialization
   */
  function init() {
    document.addEventListener('click', handleMediaProofClick);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalContainer && modalContainer.classList.contains('is-open')) {
        closeModal();
      }
    });

    // Shopify Theme Editor lifecycle integration
    document.addEventListener('shopify:section:load', () => {
      closeModal();
      stopAllMedia();
    });

    document.addEventListener('shopify:block:select', (e) => {
      const blockEl = e.target;
      const mediaProofBtn = blockEl.querySelector('[data-pt-media-proof]');
      if (mediaProofBtn && mediaProofBtn.dataset.openBehavior === 'inline') {
        const wrapper = mediaProofBtn.closest('.pt-media-proof-wrapper');
        const drawer = wrapper ? wrapper.querySelector('[data-pt-inline-drawer]') : null;
        if (drawer && drawer.hidden) {
          toggleInline(mediaProofBtn);
        }
      }
    });

    document.addEventListener('shopify:block:deselect', () => {
      closeModal();
      stopAllMedia();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
